/**
 * Ablaufplan als Balkendiagramm: Jeder Schritt ist ein Balken auf einer
 * Zeitachse. Was gleichzeitig läuft, steht untereinander. Aktive Schritte
 * sind kräftig gefärbt, Wartezeiten blass, geschätzte Dauern gestrichelt.
 */
import { formatMinutes } from '@cookbook/domain';
import type { Schedule } from '@cookbook/domain';
import { texts } from '../texts/de.ts';

/** Ablaufplan eines Rezepts. */
export function SchedulePlan({ schedule }: { readonly schedule: Schedule }) {
  if (schedule.steps.length === 0 || schedule.total <= 0) {
    return null;
  }
  const saves = schedule.sequential > schedule.total;
  const percent = (minutes: number) => `${(minutes / schedule.total) * 100}%`;
  return (
    <figure className="text-xs">
      <figcaption className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium text-content-muted">{texts.scale.plan}</span>
        <span>
          <strong>{formatMinutes(schedule.total)}</strong>
          {saves ? (
            <span className="text-content-muted">
              {' '}
              {texts.scale.planSaves.replace('{sequential}', formatMinutes(schedule.sequential))}
            </span>
          ) : null}
        </span>
      </figcaption>
      <ol className="space-y-1.5">
        {schedule.steps.map((step) => (
          <li key={step.index} className="grid grid-cols-[minmax(0,7rem)_1fr] items-center gap-2">
            <span className="truncate" title={step.label}>
              {step.index + 1}. {step.label}
            </span>
            <span className="relative h-5 rounded bg-surface-sunken">
              <span
                className={`absolute top-0 h-5 rounded ${step.active ? 'bg-accent' : 'bg-content-muted/30'} ${
                  step.estimated ? 'border border-dashed border-content-muted' : ''
                }`}
                style={{ left: percent(step.start), width: `max(6px, ${percent(step.end - step.start)})` }}
                title={`${formatMinutes(step.start)} – ${formatMinutes(step.end)}${step.estimated ? ` (${texts.scale.planEstimated})` : ''}`}
              />
            </span>
          </li>
        ))}
      </ol>
      <div className="mt-1 grid grid-cols-[minmax(0,7rem)_1fr] gap-2 text-content-muted">
        <span />
        <span className="flex justify-between">
          <span>0</span>
          <span>{formatMinutes(schedule.total)}</span>
        </span>
      </div>
    </figure>
  );
}
